import React, { useContext, useState, useEffect } from "react";
import AdminNavBar from "@/components/AdminNavBar";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, ArrowLeft, Users, Edit, Trash2, Mail, User, Key } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlayerManagement() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [editFormData, setEditFormData] = useState({
    name: "",
    email: ""
  });
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/dashboard");
    } else if (user) {
      fetchPlayers();
    }
  }, [user]);

  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/admin/players`, {
        withCredentials: true
      });
      setPlayers(response.data);
    } catch (error) {
      toast.error("Failed to load players");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEdit = (player) => {
    setSelectedPlayer(player);
    setEditFormData({
      name: player.name,
      email: player.email
    });
    setShowEditDialog(true);
  };

  const handleUpdatePlayer = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/admin/players/${selectedPlayer.id}`, editFormData, {
        withCredentials: true
      });
      toast.success("Player updated successfully");
      setShowEditDialog(false);
      fetchPlayers();
    } catch (error) {
      console.error("Update player error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to update player");
    }
  };

  const handleDeletePlayer = async (player) => {
    // Feb 2026: allow deletion of any registered player (local + Google).
    // Previously we blocked Google users here and pointed admins at Utilities;
    // the Player Management row is the natural place to do it, so both
    // paths now behave the same.
    if (!window.confirm(`Are you sure you want to delete ${player.name}? This will remove them from ALL competitions and delete all their data. This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/players/${player.id}`, {
        withCredentials: true
      });
      toast.success("Player deleted successfully");
      fetchPlayers();
    } catch (error) {
      console.error("Delete player error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to delete player");
    }
  };


  const handleResetPassword = async (player) => {
    // Check if player uses local auth
    if (player.auth_type === "google" || !player.auth_type) {
      toast.error("Cannot reset password for Google-authenticated users");
      return;
    }

    if (!window.confirm(`Reset password for ${player.name}? They will be required to change their password on next login.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/players/${player.id}/reset-password`, {}, {
        withCredentials: true
      });
      
      setSelectedPlayer(player);
      setTemporaryPassword(response.data.temporary_password);
      setShowPasswordDialog(true);
      toast.success("Password reset successfully");
    } catch (error) {
      console.error("Password reset error:", error.response?.data);
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };


  const filteredPlayers = players.filter(player =>
    player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    player.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!user || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
      <AdminNavBar />
        <div className="text-xl text-ink-muted">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      <AdminNavBar />
      {/* Header */}
      <header className="bg-white border-b border-line py-6 px-8 shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Logo variant="icon" size={36} />
            <h1 className="text-xl font-semibold text-ink tracking-tight">Golf Eclectic</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-8 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-semibold text-ink tracking-tight mb-2">Player Management</h2>
          <p className="text-lg text-ink-muted">Manage all registered players</p>
        </div>

        {/* Search and Stats */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-accent" />
                <span className="text-lg font-semibold text-ink" data-testid="total-players-count">
                  Total Players: {players.length}
                </span>
              </div>
              <div className="text-sm text-ink-muted" data-testid="players-auth-breakdown">
                Manual: {players.filter(p => p.auth_type !== "google").length} | 
                Google: {players.filter(p => p.auth_type === "google").length}
              </div>
            </div>
          </div>
          <div className="max-w-md">
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Players Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Players</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredPlayers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Type</th>
                      <th className="text-left py-3 px-4 font-semibold text-ink-muted">Competitions</th>
                      <th className="text-right py-3 px-4 font-semibold text-ink-muted">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlayers.map((player) => (
                      <tr key={player.id} className="border-b hover:bg-app" data-testid={`player-row-${player.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {player.picture ? (
                              <img
                                src={player.picture}
                                alt={player.name}
                                className="w-8 h-8 rounded-full"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-accent-tint flex items-center justify-center">
                                <User className="w-4 h-4 text-accent" />
                              </div>
                            )}
                            <span className="font-medium text-ink">{player.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 text-ink-muted">
                            <Mail className="w-4 h-4" />
                            {player.email}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            data-testid={`player-type-${player.id}`}
                            className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              player.auth_type === "google"
                                ? "bg-accent-tint text-ink"
                                : "bg-accent-tint text-accent-deep"
                            }`}
                          >
                            {player.auth_type === "google"
                              ? "Google"
                              : player.auth_type === "manual"
                                ? "Admin-created"
                                : "Email/Password"}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm text-ink-muted">
                            {player.total_competitions} competition{player.total_competitions !== 1 ? 's' : ''}
                            {player.competitions.length > 0 && (
                              <div className="mt-1">
                                {player.competitions.slice(0, 2).map(comp => (
                                  <div key={comp.id} className="text-xs text-ink-soft">
                                    • {comp.name}
                                  </div>
                                ))}
                                {player.competitions.length > 2 && (
                                  <div className="text-xs text-ink-soft">
                                    + {player.competitions.length - 2} more
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenEdit(player)}
                              data-testid={`edit-player-${player.id}`}
                              title="Edit player"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResetPassword(player)}
                              disabled={player.auth_type !== "local"}
                              className={player.auth_type !== "local" ? "opacity-50 cursor-not-allowed" : "text-warm hover:text-warm hover:bg-warm-tint/40"}
                              title={player.auth_type !== "local" ? "Cannot reset password for Google users" : "Reset password"}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeletePlayer(player)}
                              data-testid={`delete-player-${player.id}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete player"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-ink-muted">
                {searchTerm ? "No players found matching your search" : "No players registered yet"}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Player Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Player</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdatePlayer} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                required
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                required
                placeholder="john@example.com"
              />
            </div>
            <div className="bg-warm-tint/40 border border-warm/40 rounded p-3">
              <p className="text-sm text-ink-muted">
                <span className="font-semibold">Note:</span> Edits update the player&apos;s profile in the database. Google-authenticated users may see the change overridden the next time they sign in via Google.
              </p>
            </div>
            <div className="flex gap-3">
              <Button type="submit" className="flex-1 bg-ink hover:bg-ink-hover">
                Save Changes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Password Reset Successful</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-warm-tint/40 border border-warm/40 rounded-lg p-4">
              <p className="text-sm text-warm font-medium mb-2">
                ⚠️ Important: Save this password and send it to the player securely.
              </p>
              <p className="text-xs text-warm">
                This password will only be shown once. The player must change it on their next login.
              </p>
            </div>
            
            <div>
              <Label>Player Name</Label>
              <Input value={selectedPlayer?.name || ""} disabled className="mt-1" />
            </div>
            
            <div>
              <Label>Temporary Password</Label>
              <div className="flex gap-2 mt-1">
                <Input value={temporaryPassword} disabled className="font-mono" />
                <Button
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(temporaryPassword);
                    toast.success("Password copied to clipboard");
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>

            <div className="bg-app border border-line rounded-lg p-3">
              <p className="text-sm text-accent-deep">
                The player will be required to change this password when they log in.
              </p>
            </div>

            <Button
              onClick={() => {
                setShowPasswordDialog(false);
                setTemporaryPassword("");
              }}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
